import { win } from "../lib/pd-window.js";
import { getMetaContent } from "../lib/meta.js";
import { data } from "../../dev/data.js";
import { withOneTrust } from "@segment/analytics-consent-wrapper-onetrust";

const isBot = () => getMetaContent("itemprop", "bot");

export function initializeAndLoadSegment() {
  initializeSegment();
  loadSegment();
}

export function verifyCloudflareWorkersMetaTags() {
  // If isBot is null or undefined, it most likely means Cloudflare workers did not inject
  // the meta tags. This will cause Segment to not load, as well as allowing Embargoed Countries
  // to submit forms. Lets send a notification to bugsnag to track this.
  if (isBot() == null || isBot() === "") {
    win.Bugsnag.notify(
      new Error(`Cloudflare workers meta tags not detected.`),
      function (/** @type {{ severity: string; context: string; }} */ event) {
        event.context = `Cloudflare bot meta tags not detected`;
      },
    );
  }
}

/**
 * Loads segment. The function assumes that the analytics object has already
 * been initialized.
 */
export function loadSegment() {
  // This meta tag is injected from a cloudflare worker script and determines if the request
  // is coming from a known bot IP address. We do not load segment for bots.
  if (isBot() == null || isBot() === "" || isBot() === "false") {
    //Load segment script here if not a bot.
    withOneTrust(win.analytics).load(data.segmentWriteKey);
  }
}

/**
 * Loads segment. The function assumes that the analytics object has already
 * been initialized.
 */
export function loadSegmentForVendor() {
  win.analytics.load(data.segmentWriteKey);
}

/**
 * Initializes the global analytics object without loading segment, which might
 * be restricted depending on users' GDPR preferences.
 * The code comes from https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/quickstart/#step-2-add-the-segment-snippet
 * The code has been modified to use our writekey and by removing loading and
 * the initial page event, which were moved to loadSegment().
 */
export function initializeSegment() {
  const i = "analytics";
  const analytics = (win[i] = win[i] || []);
  if (analytics.initialize) return;
  if (analytics.invoked) {
    if (win.console && console.error) {
      console.error("Segment snippet included twice.");
    }
    return;
  }
  analytics.invoked = true;
  analytics.methods = [
    "trackSubmit",
    "trackClick",
    "trackLink",
    "trackForm",
    "pageview",
    "identify",
    "reset",
    "group",
    "track",
    "ready",
    "alias",
    "debug",
    "page",
    "screen",
    "once",
    "off",
    "on",
    "addSourceMiddleware",
    "addIntegrationMiddleware",
    "setAnonymousId",
    "addDestinationMiddleware",
    "register",
  ];

  /**
   *
   * @param {string} method
   * @returns
   */
  analytics.factory = function (method) {
    return function () {
      if (win[i].initialized) {
        return win[i][method].apply(win[i], arguments);
      }
      var args = Array.prototype.slice.call(arguments);
      // Add extra context for certain methods
      if (
        ["track", "screen", "alias", "group", "page", "identify"].indexOf(
          method,
        ) > -1
      ) {
        // We have removed the canonical link from the page context
        // because it overwrites the path and url in the page context
        // and we want to keep the original path and url. Think (A/B testing)
        var c = document.querySelector("link[rel='canonical']");
        args.push({
          __t: "bpc",
          c: (c && c.getAttribute("href")) || undefined, // See above comment
          p: location.pathname,
          u: location.href,
          s: location.search,
          t: document.title,
          r: document.referrer,
        });
      }
      args.unshift(method);
      analytics.push(args);
      return analytics;
    };
  };
  for (var n = 0; n < analytics.methods.length; n++) {
    var key = analytics.methods[n];
    analytics[key] = analytics.factory(key);
  }
  /**
   *
   * @param {String} key
   * @param {String} options
   */
  analytics.load = function (key, options) {
    var script = document.createElement("script");
    script.type = "text/javascript";
    script.async = true;
    script.setAttribute("data-global-segment-analytics-key", i);
    script.src =
      "https://cdn.segment.com/analytics.js/v1/" + key + "/analytics.min.js";
    var first = document.getElementsByTagName("script")[0];
    if (first && first.parentNode) {
      first.parentNode.insertBefore(script, first);
    }
    analytics._loadOptions = options;
  };
  analytics._writeKey = data.segmentWriteKey;
  analytics.SNIPPET_VERSION = "5.2.0";

  // Middlewares

  // This is a middleware that adds the canonical link to the context of the event
  // Segment added as a default in their latest snippet, but we didn't like that it
  // overwrote the canonical link in the page context, so we added it to the properties
  // and context of the event instead.
  analytics.addSourceMiddleware(addCanonicalMiddleware);

  // This is a middleware that undoes the canonical link overwrite in the page context
  // that Segment does in their loader. We need to do this because the canonical link
  // overwrites the url and path in the page context, which we don't want, as we want to
  // keep the original url and path for A/B testing purposes.
  // Run this middleware LAST, so it can undo the canonical link overwrite and not be
  // affected by other middlewares that might run after it.
  analytics.addSourceMiddleware(undoSegmentCanonicalOverwrite);

  // This now gets loaded in the Stub because we need it to fire before
  // the other events captured in the stub. (For Heap/VWO data).
  analytics.page();
}

/**
 * @typedef {Object} MiddlewarePayload
 * @property {Object} obj
 * @property {string} obj.type
 * @property {Object} [obj.context]
 */

/**
 * Since we remove Segment's automatic canonical link handling, we've decided to still capture it,
 * but in another custom property we've created called 'canonical'.
 * @param {{ payload: MiddlewarePayload, next: function(MiddlewarePayload): void }} param0
 */
function addCanonicalMiddleware({ payload, next }) {
  const eventTypesWithConsent = ["track", "page", "identify"];
  const eventType = payload.obj.type;
  if (eventTypesWithConsent.includes(eventType)) {
    const canonicalLink =
      document.querySelector("link[rel='canonical']")?.getAttribute("href") ||
      "";
    // @ts-ignore
    payload.obj.context?.page &&
      (payload.obj.context.page.canonical = canonicalLink);
  }
  next(payload);
}

/**
 * We've already commented out the canonical line in the stub, which fixes page calls, but event (track) calls are still getting the canonical value for url and path. That’s because Segment’s new snippet logic is hardcoded to do this in their loader, not just in the stub. Once the real Segment library loads, it runs its own logic to recompute the canonical and overwrite url and path in the context for all events, including track calls. That’s why the fix in the stub doesn’t stick for events, after the Segment library loads.
 * @param {{ payload: MiddlewarePayload, next: function(MiddlewarePayload): void }} param0
 */
function undoSegmentCanonicalOverwrite({ payload, next }) {
  // Only run for page and track calls
  // @ts-ignore
  if (
    payload &&
    payload.obj &&
    payload.obj.context &&
    payload.obj.context.page
  ) {
    // If canonical is present, but url/path are wrong, fix them
    // @ts-ignore
    const page = payload.obj.context.page;
    // If url/path are set to canonical, restore them to the real values
    if (
      page.canonical &&
      (page.url === page.canonical || page.path === page.canonical)
    ) {
      page.url = window.location.href;
      page.path = window.location.pathname;
    }
  }
  next(payload);
}
